import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { PreKPracticeSettings } from '../types';
import { usePreKTTS, QUESTION_SCREEN_AUDIO } from '../../../hooks/usePreKAudio';
import { NUMERAL_FONT_STYLE } from '../../PreKAssessment/preKConstants';
import {
  PracticeHeader,
  PRACTICE_PHASES,
  PRACTICE_TIMER_CONSTANTS,
  factIdToValue,
  PREK_PRACTICE_TRACKS,
} from '../shared';

interface IDoScreenProps {
  settings: PreKPracticeSettings;
  factsToPractice: string[];
  onComplete: () => void;
  onBack: () => void;
}

const IDoScreen: React.FC<IDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack
}) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [currentPhase, setCurrentPhase] = useState<'showing' | 'repeating' | 'waiting'>('waiting');
  const [autoAdvanceTimer, setAutoAdvanceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const { playTTSWithCallback, isTTSPlaying } = usePreKTTS();

  const currentFactId = factsToPractice[currentFactIndex];
  const currentDigit = currentFactId
    ? factIdToValue(PREK_PRACTICE_TRACKS.NAMING, currentFactId)
    : undefined;

  // Initialize to showing phase when component mounts or fact changes
  useEffect(() => {
    setCurrentPhase('showing');
  }, [currentFactIndex]);

  // Auto-advance to next fact after delay
  const startAutoAdvance = useCallback(() => {
    if (autoAdvanceTimer) {
      clearTimeout(autoAdvanceTimer);
    }

    const timer = setTimeout(() => {
      if (currentFactIndex < factsToPractice.length - 1) {
        // Move to next fact
        setCurrentFactIndex(currentFactIndex + 1);
        setCurrentPhase('showing');
      } else {
        // All facts completed, move to next screen
        onComplete();
      }
    }, PRACTICE_TIMER_CONSTANTS.NUMBER_NAMING_AUTO_ADVANCE_MS);

    setAutoAdvanceTimer(timer);
  }, [currentFactIndex, factsToPractice.length, onComplete, autoAdvanceTimer]);

  // Play TTS for current digit
  useEffect(() => {
    // Guard: Don't proceed if no digit is available
    if (currentDigit === undefined) {
      return;
    }

    if (currentPhase === 'showing' && settings.speechEnabled) {
      const audioFile = QUESTION_SCREEN_AUDIO.idoNumber[currentDigit as keyof typeof QUESTION_SCREEN_AUDIO.idoNumber];

      if (!audioFile) {
        setCurrentPhase('waiting');
        startAutoAdvance();
        return;
      }

      // Play "This is [number]. Say [number]." instruction
      playTTSWithCallback(audioFile, () => {
        setCurrentPhase('repeating');

        // Start auto-advance immediately after TTS completes
        setCurrentPhase('waiting');
        startAutoAdvance();
      }).catch(() => {
        console.error('❌ IDoScreen TTS failed for digit:', currentDigit);
        // If TTS fails, still advance
        setCurrentPhase('waiting');
        startAutoAdvance();
      });
    } else if (currentPhase === 'showing' && !settings.speechEnabled) {
      // If speech is disabled, still show the number and auto-advance
      setCurrentPhase('waiting');
      startAutoAdvance();
    }
  }, [currentPhase, currentDigit, settings.speechEnabled, playTTSWithCallback, startAutoAdvance, currentFactIndex]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer) {
        clearTimeout(autoAdvanceTimer);
      }
    };
  }, [autoAdvanceTimer]);

  // const getPhaseText = () => {
  //   switch (currentPhase) {
  //     case 'showing':
  //       return settings.speechEnabled ? '🔊 This is...' : 'This is...';
  //     case 'repeating':
  //       return 'Now you say it!';
  //     case 'waiting':
  //       return 'Great job! 👏';
  //     default:
  //       return '';
  //   }
  // };

  // Loading state: show loading screen if no digit is available
  if (currentDigit === undefined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.I_DO}
        onBack={onBack}
        progress={factsToPractice.length > 0 ? Math.min((currentFactIndex + 1) / factsToPractice.length, 1) : 0}
      />

      {/* Main Content Area - Centered Large Digit Display */}
      <div className="flex-1 flex items-center justify-center px-6">
        <motion.div
          key={currentDigit}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          transition={{
            duration: 0.3,
          }}
          className="w-80 h-80 bg-gradient-to-br from-blue-400 to-blue-600 rounded-3xl flex items-center justify-center shadow-2xl border-4 border-blue-700"
          style={NUMERAL_FONT_STYLE}
        >
          <span className="text-9xl font-bold text-white">
            {currentDigit}
          </span>
        </motion.div>
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Listen carefully...</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default IDoScreen;